import React, { useState } from 'react';
import {
  keepPreviousData,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import {
  Button,
  Card,
  Modal,
  Form,
  Row,
  Col,
  Pagination
} from 'react-bootstrap';
import {
  addfaqs,
  FaqsData,
  getfaqsAnswerbyProduct
} from 'api/products/_requests';
import { useSelector } from 'react-redux';
import { RootState } from 'redux-state/store';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';

interface Props {
  productId: string;
  productVariantId: string;
  vendorId: string;
}

const ProductFAQs = ({ productId, productVariantId, vendorId }: Props) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(3);
  const [expanded, setExpanded] = useState(false);
  const [showAskModal, setShowAskModal] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const queryClient = useQueryClient();
  const { user } = useSelector((state: RootState) => state.auth);

  const { data: faqsData } = useQuery<FaqsData>({
    queryKey: ['all-faqs-by-product', productId, page, limit],
    queryFn: () => getfaqsAnswerbyProduct(productId, page, limit),
    placeholderData: keepPreviousData,
    enabled: !!productId
  });

  const handleSubmit = async () => {
    try {
      const payload = {
        question: newQuestion,
        vendorId,
        productId,
        productVariantId: productVariantId || null,
        userId: user?._id
      };
      await addfaqs(payload);
      toast.success('Question added');
      queryClient.invalidateQueries({
        queryKey: ['all-faqs-by-product', productId]
      });
      queryClient.invalidateQueries({
        queryKey: ['faqs-by-vendor', vendorId]
      });
      setShowAskModal(false);
      setNewQuestion('');
    } catch {
      toast.error('Unable to add question');
    }
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Header className="bg-white py-3 px-4">
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0 fw-bold fs-0">Frequently Asked Questions</h5>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-danger"
                size="lg"
                className="fw-semibold px-3 py-1 fs--1"
                onClick={() => setShowAskModal(true)}
              >
                Ask Now
              </Button>
            </Col>
          </Row>
        </Card.Header>

        <Card.Body className="p-3 pt-2">
          {faqsData?.faqs?.map((faq, idx) => (
            <div
              key={faq._id ?? idx}
              className="pb-3 mb-2 border-bottom border-dashed"
            >
              <p className="fw-bold mb-1 fs-8">
                Q{(page - 1) * limit + idx + 1}:{' '}
                <small className="fs-8 ms-2">{faq.question}</small>
              </p>
              <p className="text-body-secondary mb-0 fs-9 ms-4">
                Ans: {faq.answer || '—'}
              </p>
            </div>
          ))}

          {!expanded && (faqsData?.total ?? 0) > 3 && (
            <div className="text-center">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setExpanded(true);
                  setLimit(5);
                  setPage(1);
                }}
              >
                View More
              </Button>
            </div>
          )}

          {expanded && (faqsData?.totalPages ?? 0) > 1 && (
            <Pagination className="mb-0 justify-content-center">
              <Pagination.Prev
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(p - 1, 1))}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Pagination.Prev>

              {[...Array(faqsData?.totalPages ?? 0)].map((_, i) => {
                const p = i + 1;
                return (
                  <Pagination.Item
                    key={p}
                    active={p === page}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </Pagination.Item>
                );
              })}

              <Pagination.Next
                disabled={page === faqsData?.totalPages}
                onClick={() =>
                  setPage(p =>
                    faqsData?.totalPages
                      ? Math.min(p + 1, faqsData.totalPages)
                      : p
                  )
                }
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Pagination.Next>
            </Pagination>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showAskModal}
        onHide={() => setShowAskModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            Ask a question about this product
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Form.Group controlId="faq-question">
              <Form.Label className="fs--1">Your Question</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={newQuestion}
                onChange={e => setNewQuestion(e.target.value)}
                placeholder="Type your question here…"
                className="fs--1"
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowAskModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={!newQuestion.trim()}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProductFAQs;
